import { useEffect, useState } from 'react';
import { Database, HardDrive, Mail, Scale } from 'lucide-react';
import { api } from '@/lib/api';

// The model badge reads the live model from the api's health endpoint (which
// reflects ANTHROPIC_MODEL) instead of hardcoding a model name that goes
// stale on every model switch. Fetched once per page load and cached at
// module level so remounts don't refetch.
let cachedLabel: string | null = null;
let labelPromise: Promise<string> | null = null;

function fetchModelLabel(): Promise<string> {
  if (cachedLabel !== null) {
    return Promise.resolve(cachedLabel);
  }
  labelPromise ??= api<{ status: string; modelLabel?: string }>('/health')
    .then((h) => {
      cachedLabel = h.modelLabel ?? '';
      return cachedLabel;
    })
    .catch(() => {
      labelPromise = null;
      return '';
    });
  return labelPromise;
}

// Header wordmark: gradient amber square holding the Scale icon, with the
// Court Copilot wordmark in Instrument Serif italic and a tiny capability
// strip beneath. Defined in 03-ui-design-language.md "Brand mark".
export function BrandMark() {
  const [modelLabel, setModelLabel] = useState(cachedLabel ?? '');

  useEffect(() => {
    let cancelled = false;
    void fetchModelLabel().then((label) => {
      if (!cancelled) {
        setModelLabel(label);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-amber-500 to-amber-700 shadow-md shadow-amber-900/30">
        <Scale className="h-5 w-5 text-stone-950" strokeWidth={2.5} />
      </div>
      <div className="flex flex-col leading-tight">
        <span className="font-display text-xl italic text-stone-100">Court Copilot</span>
        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-stone-500">
          <span className="text-amber-500">{modelLabel || '—'}</span>
          <span aria-hidden>·</span>
          <Database className="h-3 w-3" aria-label="Postgres" />
          <Mail className="h-3 w-3" aria-label="Microsoft 365" />
          <HardDrive className="h-3 w-3" aria-label="Dropbox" />
        </span>
      </div>
    </div>
  );
}
